/**
 * Invoice Service Catalog foundation.
 *
 * Expandable catalog of billable services that are not tied to labour or
 * materials (website deposits, hosting, consulting, etc.).
 *
 * Phase 1 ships a built-in default catalog. Later this can load from a
 * dedicated `invoice_services` table without changing call sites.
 */

export const DEFAULT_INVOICE_SERVICE_CATALOG = Object.freeze([
  Object.freeze({
    service_id: "website-deposit",
    name: "Website Deposit",
    description: "Deposit toward website design and development.",
    default_unit_price: 0,
    default_quantity: 1,
    unit_label: "ea",
    taxable: true,
  }),
  Object.freeze({
    service_id: "website-design",
    name: "Website Design",
    description: "Website design services.",
    default_unit_price: 0,
    default_quantity: 1,
    unit_label: "ea",
    taxable: true,
  }),
  Object.freeze({
    service_id: "website-development",
    name: "Website Development",
    description: "Website development and build services.",
    default_unit_price: 0,
    default_quantity: 1,
    unit_label: "ea",
    taxable: true,
  }),
  Object.freeze({
    service_id: "monthly-care-plan",
    name: "Monthly Care Plan",
    description: "Recurring website care and maintenance plan.",
    default_unit_price: 0,
    default_quantity: 1,
    unit_label: "mo",
    taxable: true,
  }),
  Object.freeze({
    service_id: "hosting",
    name: "Hosting",
    description: "Website hosting.",
    default_unit_price: 0,
    default_quantity: 1,
    unit_label: "mo",
    taxable: true,
  }),
  Object.freeze({
    service_id: "consulting",
    name: "Consulting",
    description: "Consulting services.",
    default_unit_price: 0,
    default_quantity: 1,
    unit_label: "hrs",
    taxable: true,
  }),
  Object.freeze({
    service_id: "travel-charge",
    name: "Travel Charge",
    description: "Travel / site visit charge.",
    default_unit_price: 0,
    default_quantity: 1,
    unit_label: "ea",
    taxable: true,
  }),
  Object.freeze({
    service_id: "emergency-service",
    name: "Emergency Service",
    description: "Emergency or after-hours service call.",
    default_unit_price: 0,
    default_quantity: 1,
    unit_label: "ea",
    taxable: true,
  }),
  Object.freeze({
    service_id: "custom-service",
    name: "Custom Service",
    description: "Custom billable service.",
    default_unit_price: 0,
    default_quantity: 1,
    unit_label: "ea",
    taxable: true,
  }),
]);

/**
 * Load the invoice service catalog.
 * Accepts optional extra entries (e.g. org overrides) for future expansion.
 */
export async function loadInvoiceServiceCatalog({ extras = [] } = {}) {
  const byId = new Map();

  for (const entry of DEFAULT_INVOICE_SERVICE_CATALOG) {
    byId.set(entry.service_id, { ...entry });
  }

  for (const entry of extras || []) {
    if (!entry?.service_id || !entry?.name) continue;
    byId.set(entry.service_id, {
      service_id: entry.service_id,
      name: entry.name,
      description: entry.description || "",
      default_unit_price: Number(entry.default_unit_price) || 0,
      default_quantity: Number(entry.default_quantity) || 1,
      unit_label: entry.unit_label || "ea",
      taxable: entry.taxable !== false,
    });
  }

  return [...byId.values()].sort((a, b) => a.name.localeCompare(b.name));
}

export function findInvoiceServiceById(catalog, serviceId) {
  return (catalog || []).find((entry) => entry.service_id === serviceId) || null;
}
